import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { SortDirectionEnum } from '../enums/sort-direction.enum';
import { ESEService } from '../ese.service';
import { ESE_ENUM } from '../enums/ese.enum';
import { ProbabilityTextEnum } from '../enums/impact.enum';
import { Animations } from 'app/risk-register/risk-table/animations';
import { NgbActiveModal, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ModalTemplateComponent } from '../modal-template/modal-template.component';
// eslint-disable-next-line max-len
import { CreateEseFromTemplateModalComponent } from 'app/third-party/create-ese-from-template-modal/create-ese-from-template-modal.component';

@Component({
  selector: 'cygov-emerging-security-event-table',
  templateUrl: './emerging-security-event-table.component.html',
  styleUrls: ['./emerging-security-event-table.component.scss'],
  animations: [Animations.riskDetailOpenAnimation],
  standalone: false,
})
export class EmergingSecurityEventTableComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Input() headerItemsESE: any;
  @Input() isFromVendorListing: boolean = false;
  @Input() isCreateFromTemplate: boolean = false;
  @Input() initialErrorObject: any;
  currentSortedColumn = 'Question Num';
  reverseDirection: SortDirectionEnum;
  sortDirections: any;
  @Input() data: any = [];
  @Input() selectedData: any = [];
  @Input() isEditMode: boolean;
  @Input() keysObject: any;
  @Input() validationErrors: { [rowIndex: number]: { [key: string]: boolean } } = {};
  @Input() isLoading: boolean = false;
  // placeholder rows shown while the questionnaire loads or is being replaced by an upload
  readonly skeletonRows = [1, 2];
  // guards the one un-animated placement made when the skeletons give way to real rows
  private hasPlacedAddButton = false;
  entityId: string = '';
  isAfterMid: boolean = false;
  openedNameIndex: number = -1;
  @ViewChild('tableWrapper') tableWrapper!: ElementRef<HTMLDivElement>;
  cachedData: any = [];

  isLeftArrowDisabled = true;
  isRightArrowDisabled = false;
  scrollAmount = 200; // Adjust scroll distance per click as needed
  duration = 300; // Duration of the scroll animation in milliseconds
  rowHeight = 9; // Height of each row in vh (from CSS)
  tableMaxHeight = 56; // Max height of table in vh (from CSS)
  headerHeight = 16; // Approximate height of header in vh (adjust as needed)
  addButtonPadding = 4;
  booleanOptions = ['True', 'False'];
  severityList = Object.keys(ProbabilityTextEnum)
    .map(prob => ProbabilityTextEnum[prob])
    .sort((a, b) => (ProbabilityTextEnum[a] > ProbabilityTextEnum[b] ? 1 : -1));
  @Output() addData = new EventEmitter<any>();
  @Output() updateData = new EventEmitter<any>();
  @Output() setSelectedRow = new EventEmitter<any>();
  currentScrollPosition: number = 0;
  originalRowIndex: number | null = null;
  currentlyOpenRow: number | null = null;
  isUploaderActive: boolean = false;
  @Output() onUploadArtifact = new EventEmitter<any>();
  uploaderStepIndex: number = 0;
  assessmentLevel = {};
  @Output() removeArtifact = new EventEmitter<any>();
  closeModalInstance: any;
  ngbModalOptionsUploadTemplate: NgbModalOptions = {
    size: 'sm',
    windowClass:
      'modal-template-cont confirmation-filter-popup-window ese-template-questionnaire-uplaod-failed-modal-window',
    backdropClass:
      'modal-template-cont-backdrop risk-popup-backdrop-modal ese-template-questionnaire-uplaod-failed-modal-backdrop',
    backdrop: 'static',
    centered: true,
  };
  ngbModalOptionsESEModal: NgbModalOptions = {
    size: 'sm',
    windowClass: 'create-ese-from-template-modal-window',
    backdropClass: 'create-ese-from-template-modal-backdrop',
    backdrop: 'static',
  };
  @Input() failedRows: any = [];
  @Output() downLoad = new EventEmitter<any>();
  hideAddButton = true;
  uploadedFiles: File[] = [];
  progressValues: number[] = [];

  selectedRowIndex = null;
  searchQuery: any;

  /**
   * Placeholder width per column, so a skeleton row reads like the text it stands in for rather
   * than a run of identical bars. Same convention as the Risk and Policies tables.
   */
  skeletonWidthFor(name: string): string {
    switch (name) {
      case ESE_ENUM.QUESTION:
      case ESE_ENUM.REMEDIATION_TASK:
        return '85%';
      case ESE_ENUM.SUPPLEMETED_GUIDE:
        return '75%';
      case 'Question Num':
      case ESE_ENUM.SEVERITY:
        return '45%';
      default:
        return '65%';
    }
  }

  constructor(
    private eseService: ESEService,
    private modalService: NgbModal,
    public activeModal: NgbActiveModal
  ) {}
  ngOnInit() {
    this.cachedData = JSON.parse(JSON.stringify(this.eseService.cachedQuestions));
    if (this.isFromVendorListing) {
      this.headerHeight = 12;
      this.tableMaxHeight = 47;
      this.addButtonPadding = 6;
    }
    if (this.isCreateFromTemplate) {
      this.tableMaxHeight = 35;
      this.headerHeight = 12;
      this.addButtonPadding = 4;
    }
    this.initializeData();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && changes.data.currentValue) {
      this.cachedData = JSON.parse(JSON.stringify(this.eseService.cachedQuestions));
      this.updateAddButtonPosition();
    }
    if (changes.isEditMode && !changes.isEditMode.firstChange) {
      const nowEditing = changes.isEditMode.currentValue;
      const wasEditing = changes.isEditMode.previousValue;
      // Exiting edit mode (e.g. the X/close button in the action bar, which only emits
      // enableEdit(false)) must tear down any open uploader. Otherwise `isUploaderActive`
      // stays true and horizontal scrolling remains disabled for the rest of the session.
      if (wasEditing && !nowEditing && this.isUploaderActive) {
        this.resetUploaderState();
      } else if (!wasEditing && nowEditing) {
        // Entering edit mode must never auto-show an uploader that was left open in
        // previously-saved data. SAVE persists rows while the uploader may still be
        // open, so `isUploaderOpen` can be baked into cachedQuestions; clear it here.
        this.clearStaleUploaderFlags();
      }
    }
  }

  /**
   * Clear transient artifact-uploader flags on the current rows so a stale
   * `isUploaderOpen` (persisted into saved data) can't auto-open the uploader
   * section when edit mode is re-entered without clicking the upload icon.
   */
  private clearStaleUploaderFlags(): void {
    if (Array.isArray(this.data)) {
      this.data.forEach(row => {
        if (row) {
          row.isUploaderOpen = false;
          row.originalIndex = null;
        }
      });
    }
    this.isUploaderActive = false;
    this.currentlyOpenRow = null;
    this.selectedRowIndex = null;
    this.uploaderStepIndex = 0;
  }

  /**
   * Reset the artifact-uploader state so horizontal scrolling is re-enabled.
   * If the reordered open row is still present in `data`, close it properly to
   * restore its original position; otherwise the parent has already replaced `data`
   * (edit-off resets questions from cache), so just clear the scroll-gating flags.
   */
  private resetUploaderState(): void {
    const openIndex = this.currentlyOpenRow;
    if (openIndex !== null && this.data?.[openIndex]?.isUploaderOpen) {
      this.closeUploader(openIndex);
      return;
    }
    this.isUploaderActive = false;
    this.currentlyOpenRow = null;
    this.selectedRowIndex = null;
    this.uploaderStepIndex = 0;
    this.isLeftArrowDisabled = this.isRightArrowDisabled = false;
    this.checkScrollPosition();
  }
  ngAfterViewInit() {
    this.checkScrollPosition(); // Check initial scroll position
    // Listen for scroll events to update arrow states
    this.tableWrapper.nativeElement.addEventListener('scroll', () => this.checkScrollPosition());
    this.updateAddButtonPosition();
  }
  initializeData() {
    if (Array.isArray(this.data)) {
      this.data = this.data.map(item => ({
        ...item,
        isUploaderOpen: false,
        originalIndex: null,
        isSelected: false,
      }));
    }
    this.cachedData = JSON.parse(JSON.stringify(this.eseService.cachedQuestions));
    this.hideAddButton = false;
  }

  closeArtifactUploader() {
    // Close the row that is actually open: after toggleUploader() the open row is
    // moved to the front and tracked by `currentlyOpenRow` (0), whereas
    // `selectedRowIndex` holds its ORIGINAL index — using that would close the wrong
    // row and leave the real one with isUploaderOpen=true / an unrestored position.
    if (this.currentlyOpenRow !== null) {
      this.closeUploader(this.currentlyOpenRow);
    }
  }
  toggleUploader(index: number): void {
    this.uploaderStepIndex = 0;
    if (this.currentlyOpenRow !== null && this.currentlyOpenRow !== index) {
      // close the cuurently open uplaoder if a different row is selected
      this.closeUploader(this.currentlyOpenRow);
    }
    this.selectedRowIndex = index;
    const row = this.data[index];
    if (row.isUploaderOpen) {
      this.closeUploader(index);
      this.selectedRowIndex = null;
      return;
    }

    this.currentScrollPosition = this?.tableWrapper?.nativeElement?.scrollTop || 0;
    row.isUploaderOpen = true;
    row.originalIndex = index;

    const removedRow = this.data.splice(index, 1)[0];
    this.data.unshift(removedRow);
    this.currentlyOpenRow = 0;
    if (this.tableWrapper?.nativeElement) {
      const wrapper = this.tableWrapper.nativeElement;
      const farRightPosition = wrapper.scrollWidth - wrapper.clientWidth;
      const currentPosition = wrapper.scrollLeft;
      const delta = farRightPosition - currentPosition;
      this.smoothScroll(delta);
    }
    this.tableWrapper.nativeElement.scrollTop = 0;
    this.isUploaderActive = true;
  }

  closeUploader(index: number) {
    const row = this.data[index];
    row.isUploaderOpen = false;
    this.currentlyOpenRow = null;
    this.isUploaderActive = false;

    const originalIndex = row.originalIndex;
    if (originalIndex !== null) {
      const removedRow = this.data.splice(index, 1)[0];
      this.data.splice(originalIndex, 0, removedRow);
      row.originalIndex = null;
      this.tableWrapper.nativeElement.scrollTop = this.currentScrollPosition;
    }
    this.isRightArrowDisabled = this.isLeftArrowDisabled = false;

    // reset the uploader step index
    this.uploaderStepIndex = 0;
    this.checkScrollPosition();
  }

  updateAddButtonPosition() {
    const addButton = document.getElementById('addQuestionButton');
    if (!addButton) {
      console.warn('addQuestionButton not found in the DOM');
      return;
    }
    const rowCount = this.data.length;
    const totalRowsHeight = rowCount * this.rowHeight;
    const top =
      totalRowsHeight < this.tableMaxHeight - this.headerHeight
        ? // Button stays inside the table, moves down with each row
          `${this.headerHeight + rowCount * this.rowHeight}vh`
        : // Button moves below the last row and above the hr line (4vh padding above hr)
          `${this.tableMaxHeight + this.addButtonPadding}vh`;

    // The first placement once the rows are real must not animate: the button is coming out of
    // hiding and would otherwise be seen sliding down from under the header to its true place.
    // The transition is restored straight afterwards, for rows the user adds later.
    if (!this.hasPlacedAddButton && !this.isLoading) {
      this.hasPlacedAddButton = true;
      addButton.style.transition = 'none';
      addButton.style.top = top;
      requestAnimationFrame(() => (addButton.style.transition = ''));
      return;
    }
    addButton.style.top = top;
  }

  scrollLeft() {
    this.smoothScroll(-this.scrollAmount);
  }

  scrollRight() {
    this.smoothScroll(this.scrollAmount);
  }

  smoothScroll(delta: number) {
    const wrapper = this.tableWrapper.nativeElement;
    const start = wrapper.scrollLeft;
    const end = start + delta;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / this.duration, 1); // Ensure progress doesn't exceed 1
      const ease = this.easeInOutQuad(progress); // Apply easing function
      wrapper.scrollLeft = start + (end - start) * ease;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.checkScrollPosition(); // Update arrow states after animation
      }
    };

    requestAnimationFrame(animate);
  }

  // Easing function for smooth transition
  easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  checkScrollPosition() {
    const wrapper = this.tableWrapper.nativeElement;
    const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;

    // Disable left arrow if at the start
    this.isLeftArrowDisabled = wrapper.scrollLeft <= 0;

    // Disable right arrow if at the end
    this.isRightArrowDisabled = wrapper.scrollLeft >= maxScroll - 1; // Small buffer for precision
  }
  /**
   * This function will add the new empty row in screen and you can update the
   * empty question object as per your need
   */
  addRow() {
    this.addData.emit();
    this.updateAddButtonPosition();
  }

  update(event, key, index) {
    this.data[index][key] = event.target.value;
    this.updateData.emit({ value: event.target.value, key, index });
  }
  selectRow(index, $event) {
    this.setSelectedRow.emit({ index, isChecked: $event });
  }

  showMoreState: { [key: number]: { [key: string]: boolean } } = {};

  toggleShowMore(event: MouseEvent, idx: number, type: string) {
    const screenMid = window.screen.height / 2 + window.screen.height * 0.07;
    this.isAfterMid = event.clientY > screenMid;

    // Initialize the state for the row if it doesn't exist
    if (!this.showMoreState[idx]) {
      this.showMoreState[idx] = {};
    }

    // Close all previously opened popups in the same row
    if (this.showMoreState[idx][type]) {
      Object.keys(this.showMoreState[idx] || {}).forEach(key => {
        this.showMoreState[idx][key] = false;
      });
    } else {
      Object.keys(this.showMoreState[idx] || {}).forEach(key => {
        this.showMoreState[idx][key] = false;
      });
      this.showMoreState[idx][type] = true;
    }

    this.openedNameIndex = idx;
  }

  sortData(colName, index, direction, childIndex = -1) {
    let directionFactor;
    const fieldName = colName === ESE_ENUM.QUESTION_NUM ? ESE_ENUM.QUESTION_NUMBER : colName;
    if (this.headerItemsESE[index].isGroup && childIndex > -1) {
      const child = this.headerItemsESE[index].children[childIndex];
      child.sortDirection =
        direction === SortDirectionEnum.ASCENDING ? SortDirectionEnum.DESCENDING : SortDirectionEnum.ASCENDING;
      // To handle asc or dsc order sorting
      directionFactor = child.sortDirection === SortDirectionEnum.ASCENDING ? 1 : -1;
    } else {
      this.headerItemsESE[index].sortDirection =
        direction === SortDirectionEnum.ASCENDING ? SortDirectionEnum.DESCENDING : SortDirectionEnum.ASCENDING;
      // To handle asc or dsc order sorting
      directionFactor = this.headerItemsESE[index].sortDirection === SortDirectionEnum.ASCENDING ? 1 : -1;
    }

    this.data.sort((a, b) => {
      const valA = a[fieldName];
      const valB = b[fieldName];
      // Number comparison
      if (typeof valA === 'number' && typeof valB === 'number') {
        return (valA - valB) * directionFactor;
      }
      // String comparison
      if (typeof valA === 'string' && typeof valB === 'string') {
        return valA.localeCompare(valB) * directionFactor;
      }
      // Boolean Comparison
      if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        return (Number(valA) - Number(valB)) * directionFactor;
      }

      return 0;
    });
  }

  uploadArtifact(event: Event, index): void {
    this.uploaderStepIndex++;
    this.uploadedFiles = [];
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      for (const file of Array.from(input.files)) {
        // Optimistically update UI so icon/state changes immediately
        const row = this.data[index];
        if (row) {
          if (!row[this.keysObject.template] || row[this.keysObject.template] === '') {
            row[this.keysObject.template] = 'ESE_ARTIFACT';
          }
        }

        this.onUploadArtifact.emit({ file, rowIndex: index });
        this.uploadedFiles.push(file);
        this.progressValues[index] = 0;
        this.animateProgress(index, file.size);
      }
    }
  }

  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase();
  }

  animateProgress(index: number, fileSize: number): void {
    const speedFactor = Math.max(1, Math.floor(fileSize / (5 * 1024 * 1024)));
    // Larger files → slower animation

    const interval = setInterval(() => {
      if (!this.uploadedFiles[index]) {
        return clearInterval(interval);
      }

      if (this.progressValues[index] < 100) {
        const increment = Math.random() * (8 / speedFactor) + 1; // fake step
        this.progressValues[index] = Math.min(this.progressValues[index] + increment, 100);
      } else {
        this.uploaderStepIndex = 2;
        // this.isFileUploaded = true;
        clearInterval(interval);
      }
    }, 200);
  }
  calculateProgress(name: string): void {
    this.assessmentLevel[name] = 0;
    const x = setInterval(() => {
      this.assessmentLevel[name] += 30;
      if (this.assessmentLevel[name] >= 100) {
        clearInterval(x);
        this.uploaderStepIndex++;
      }
    }, 500);
  }
  onSelectionChange(event, key, index) {
    this.updateData.emit({ value: event, key, index });
  }

  validateAndFormat(event: Event, key: string, index: number): void {
    const inputEl = event.target as HTMLInputElement;
    const value = inputEl.value.trim().toLowerCase();
    if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
      const upperValue = this.eseService.capitalizeFirstLetter(value);
      this.data[index][key] = upperValue;
      inputEl.value = upperValue; // update visible input
    }
  }
  removeFile(questionIndex, fileIndex = null) {
    // Optimistically update UI so icon/state changes immediately
    const row = this.data[questionIndex];
    if (row) {
      if (fileIndex === null) {
        row[this.keysObject.template] = '';
      }
    }

    this.removeArtifact.emit({ questionIndex, fileIndex });
    this.uploadedFiles.splice(fileIndex, 1);
    if (this.uploadedFiles.length === 0) {
      this.uploaderStepIndex = 0;
    }
  }
  filterQuestions() {
    const term = this.searchQuery.toLowerCase();
    if (term !== '') {
      this.data = this.eseService.cachedQuestions.filter(
        q =>
          q[this.keysObject.function].toLowerCase().includes(term) ||
          q[this.keysObject.category].toLowerCase().includes(term) ||
          q[this.keysObject.subCategory].toLowerCase().includes(term) ||
          q[this.keysObject.controlLabel].toLowerCase().includes(term) ||
          q[this.keysObject.controlName].toLowerCase().includes(term) ||
          q[this.keysObject.question].toLowerCase().includes(term) ||
          q[this.keysObject.remediationTask].toLowerCase().includes(term) ||
          q[this.keysObject.supplementedGuide].toLowerCase().includes(term) ||
          q[this.keysObject.severity].toLowerCase().includes(term) ||
          q[this.keysObject.supplementedGuide].toLowerCase().includes(term) ||
          q[this.keysObject.mandatoryArtifacts].toLowerCase().includes(term) ||
          q[this.keysObject.mandatoryComments].toLowerCase().includes(term)
      );
    } else {
      this.data = JSON.parse(JSON.stringify(this.eseService.cachedQuestions));
    }
  }
  openRejectedListModal(header: TemplateRef<any>, content: TemplateRef<any>, footer: TemplateRef<any>) {
    const modalRef = this.modalService.open(ModalTemplateComponent, this.ngbModalOptionsUploadTemplate);
    const compInstance = modalRef.componentInstance;

    this.closeModalInstance = modalRef;

    compInstance.customizeHeader = true;
    compInstance.headerContentRef = header;
    compInstance.contentRef = content;
    compInstance.footerContentRef = footer;
  }

  closeModal() {
    if (this.closeModalInstance) {
      this.closeModalInstance.close();
    }
  }
  openCreatFromTemplateModal() {
    this.closeModal();
    this.activeModal.close();
    const modalRef = this.modalService.open(CreateEseFromTemplateModalComponent, this.ngbModalOptionsESEModal);
    modalRef.componentInstance.entity = this.eseService.entity;
    modalRef.componentInstance.selectedVendors = this.eseService.selectedVendors;
    modalRef.componentInstance.currentUser = this.eseService.currentUser;
  }
  onDownload() {
    this.downLoad.emit();
  }

  get clearedRowsCount(): number {
    const savedRowsCount = Array.isArray(this.eseService.cachedQuestions) ? this.eseService.cachedQuestions.length : 0;
    // Anchor to the last saved rows so a row added via "Add Question" (not yet part of
    // cachedQuestions) never counts as Cleared until Save persists it.
    const failedWithinSaved = Array.isArray(this.failedRows)
      ? this.failedRows.filter(rowIndex => rowIndex < savedRowsCount).length
      : 0;
    const clearedCount = savedRowsCount - failedWithinSaved;
    return clearedCount < 0 ? 0 : clearedCount;
  }
}
